import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://placeholder.supabase.co';
const supabasePublishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || 'placeholder-key';

if (import.meta.env.DEV && supabaseUrl.includes('placeholder')) {
  console.error('[EB] Supabase URL is a placeholder -- env vars not loaded. Check Netlify account.');
}

const isPreview = Boolean(import.meta.env.VITE_PREVIEW_PR);
let previewActive = !isPreview;
let everActivated = false;
let releaseInitialRequests: () => void = () => {};
const initialReadiness = new Promise<void>(resolve => { releaseInitialRequests = resolve; });

// Supabase can refresh a stored session during module initialization, before
// React providers mount. Hold those requests until the generation gate approves.
export const supabase = createClient(supabaseUrl, supabasePublishableKey, isPreview ? {
  auth: { autoRefreshToken: false },
  global: { fetch: async (input, init) => {
    if (!everActivated) await initialReadiness;
    if (!previewActive) throw new Error('Preview database is asleep');
    return fetch(input, init);
  } },
} : undefined);
export function setPreviewDatabaseActive(active: boolean) {
  if (!isPreview) return;
  previewActive = active;
  if (active) {
    everActivated = true;
    releaseInitialRequests();
    supabase.auth.startAutoRefresh();
  } else {
    supabase.auth.stopAutoRefresh();
  }
}

type TestRealtimeFilter = {
  event?: string;
  schema?: string;
  table?: string;
  filter?: string;
};

type TestRealtimeBinding = {
  channel: unknown;
  channelName: string;
  filter: TestRealtimeFilter;
  callback: (payload: unknown) => void;
};

declare global {
  interface Window {
    __EB_TEST_REALTIME__?: {
      emitPostgresChange: (filter: TestRealtimeFilter, payload?: Record<string, unknown>) => number;
      bindings: TestRealtimeBinding[];
    };
  }
}

if (import.meta.env.DEV && typeof window !== 'undefined' && !window.__EB_TEST_REALTIME__) {
  const bindings: TestRealtimeBinding[] = [];
  const originalChannel = supabase.channel.bind(supabase);
  const originalRemoveChannel = supabase.removeChannel.bind(supabase);

  supabase.channel = ((channelName: string, options?: Parameters<typeof supabase.channel>[1]) => {
    const channel = originalChannel(channelName, options);
    const originalOn = channel.on.bind(channel);

    channel.on = ((type: string, filter: TestRealtimeFilter, callback: (payload: unknown) => void) => {
      if (type === 'postgres_changes') {
        bindings.push({ channel, channelName, filter, callback });
      }
      return originalOn(type as never, filter as never, callback as never);
    }) as typeof channel.on;

    return channel;
  }) as typeof supabase.channel;

  supabase.removeChannel = ((channel: Parameters<typeof supabase.removeChannel>[0]) => {
    for (let i = bindings.length - 1; i >= 0; i -= 1) {
      if (bindings[i].channel === channel) bindings.splice(i, 1);
    }
    return originalRemoveChannel(channel);
  }) as typeof supabase.removeChannel;

  window.__EB_TEST_REALTIME__ = {
    bindings,
    emitPostgresChange(filter, payload = {}) {
      const schema = filter.schema ?? 'public';
      const event = filter.event ?? 'UPDATE';
      let delivered = 0;

      for (const binding of [...bindings]) {
        const bindingEvent = binding.filter.event ?? '*';
        const eventMatches = bindingEvent === '*' || bindingEvent === event || filter.event == null;
        const schemaMatches = (binding.filter.schema ?? 'public') === schema;
        const tableMatches = !filter.table || binding.filter.table === filter.table;
        if (!eventMatches || !schemaMatches || !tableMatches) continue;

        binding.callback({
          eventType: event,
          schema,
          table: filter.table ?? binding.filter.table,
          commit_timestamp: new Date().toISOString(),
          errors: null,
          new: {},
          old: {},
          ...payload,
        });
        delivered += 1;
      }

      return delivered;
    },
  };
}
